import { Environment } from "../environment";
import { Scope, SeverityLevel } from "@sentry/core";

export class Logger {
  private get Sentry() {
    return Environment.Sentry;
  }

  public captureException = (
    exc: Error,
    level: SeverityLevel = "error",
    callback?: (scope: Scope) => void
  ) => {
    if (__DEV__) {
      console.groupCollapsed("Sentry exception");
      console.warn(exc);
      console.groupEnd();
    }

    // Capture exception with sentry
    this.Sentry?.withScope((scope: Scope) => {
      scope.setLevel(level.toLowerCase() as SeverityLevel);
      callback?.(scope);
      this.Sentry?.captureException(exc);
    });
  };

  public captureMessage = (
    message: string,
    level: SeverityLevel = "log",
    callback?: (scope: Scope) => void
  ) => {
    if (__DEV__) {
      console.groupCollapsed("Sentry warning");
      console.warn(message);
      console.groupEnd();
    }

    // Capture message with sentry
    this.Sentry?.withScope((scope: Scope) => {
      scope.setLevel(level.toLowerCase() as SeverityLevel);
      callback?.(scope);
      this.Sentry?.captureMessage(message);
    });
  };

  public captureObject(
    title: string,
    obj: any,
    level: SeverityLevel = "log",
    callback?: (scope: Scope) => void
  ) {
    // Capture message with sentry

    if (__DEV__) {
      console.groupCollapsed(`Sentry object capture: ${title}`);
      console.log(obj);
      console.groupEnd();
    }

    this.Sentry?.withScope((scope: Scope) => {
      scope.setLevel(level.toLowerCase() as SeverityLevel);
      scope.setExtra("data", obj);
      callback?.(scope);
      this.Sentry?.captureMessage(title);
    });
  }

  public capture = (
    event: Error | string,
    level?: SeverityLevel,
    callback?: (scope: Scope) => void
  ) => {
    if (event instanceof Error) {
      this.captureException(event, level, callback);
    } else {
      this.captureMessage(event, level, callback);
    }
  };

  public debug(log: string) {
    this.capture(log, "debug");
  }

  public info(log: string) {
    this.capture(log, "info");
  }

  public warn(log: string) {
    this.capture(log, "warning");
  }

  public error(log: string) {
    this.capture(log, "error");
  }

  public dev(message: any, group = "generic") {
    if (__DEV__) {
      if (typeof message === "string") {
        console.log(message);
      } else {
        console.groupCollapsed(`Dev object capture: ${group}`);
        console.log(message);
        console.groupEnd();
      }
    }
  }
}

export const DefaultLogger = new Logger();
